<template>
    <div class="pc-preview-control-content">
        <div class="pc-image-container" ref="imageview">
            <VueZoomable class="pc-image-wrapper" selector="#pc-image" :minZoom="0.1" :maxZoom="10" :zoom="1">
                <img id="pc-image" class="pi-image" ref="image" draggable="false" />
            </VueZoomable>
        </div>

        <div class="pc-info-container" ref="controlview">
            <div ref="info">
                <div class="wp-block-column is-layout-flow wp-block-column-is-layout-flow">
                    <h1 class="wp-block-post-title">{{ productTitle }}</h1>
                    <p class="wp-block-post-excerpt__excerpt">
                        {{ infoText }}
                    </p>
                    <div
                        data-block-name="woocommerce/product-price"
                        data-font-size="large"
                        data-is-descendent-of-single-product-template="true"
                        class="wp-block-woocommerce-product-price"
                    >
                        <div
                            class="wc-block-components-product-price wc-block-grid__product-price has-text-align- has-font-size has-large-font-size has-text-align-"
                            style=""
                        >
                            <span class="woocommerce-Price-amount amount">
                                <bdi>
                                    <span class="woocommerce-Price-currencySymbol">
                                        {{ currency }}
                                    </span>
                                    &nbsp;{{ formattedPrice }}
                                </bdi>
                            </span>
                        </div>
                    </div>

                    <div
                        data-block-name="woocommerce/add-to-cart-form"
                        class="wp-block-add-to-cart-form wc-block-add-to-cart-form wc-block-add-to-cart-form--input wp-block-woocommerce-add-to-cart-form"
                    >
                        <div class="quantity">
                            <input
                                ref="quantityInputField"
                                type="number"
                                class="input-text qty text"
                                name="quantity"
                                value="1"
                                min="1"
                                max=""
                                step="1"
                                placeholder=""
                                inputmode="numeric"
                                autocomplete="off"
                                style="min-width: 100px"
                            />
                        </div>
                    </div>

                    <p v-if="uploadError" class="pc-upload-error" role="alert">
                        {{ uploadError }}
                    </p>

                    <button
                        v-if="allowImageDownload"
                        type="button"
                        class="pc-download-button"
                        @click="downloadImage"
                    >
                        {{ imageDownloadButtonText }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="isUploading" class="pc-loading-overlay" role="status" aria-live="polite">
            <span class="pc-spinner" aria-hidden="true"></span>
        </div>
    </div>
</template>

<script>
import { inject } from "vue";
import VueZoomable from "vue-zoomable";
import { Buffer } from "buffer";
import bmp from "bmp-js";
import "vue-zoomable/dist/style.css";
window.Buffer = Buffer; // Workaround for image-bmp package

export default {
    name: "PreviewControl",
    components: { VueZoomable },
    setup() {
        // Inject dependencies
        const config = inject("config");

        // Get designer variant
        const designerVariant = config.product_variants.find(
            (variant) => Number(variant.id) === Number(config.product.productVariantId)
        );
        if (!designerVariant) {
            console.error("Designer variant not found");
        }

        const productTitle = config.product.productTitle;
        const infoText = config.texts.previewView.infoText;
        const currency = config.product.currency;
        const price = config.product.productPrice;
        const formattedPrice = Number(price).toFixed(2);

        const allowImageDownload = config.texts.previewView.allowImageDownload === true;
        const imageDownloadButtonText = config.texts.previewView.imageDownloadButtonText || "Bild herunterladen";

        return {
            config,
            designerVariant,
            productTitle,
            infoText,
            currency,
            formattedPrice,
            allowImageDownload,
            imageDownloadButtonText,
        };
    },
    data() {
        return {
            previewImage: null,
            distortedImage: null,
            designerVariant: null,

            isPortrait: false,
            isUploading: false,
            uploadError: null,
        };
    },
    methods: {
        setImageBase64(base64) {
            this.distortedImage = new Image();
            this.distortedImage.src = base64;
            this.updateImage();
        },

        getImage() {
            return this.distortedImage;
        },

        getImageBase64() {
            return this.distortedImage.src;
        },

        updateImage() {
            const imgRef = this.$refs.image;
            const base64 = this.getImageBase64();

            // Create a temporary canvas for resizing
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            let output_width = 0;
            let output_height = 0;

            if (this.isPortrait) {
                output_width = this.designerVariant.output_image.height / this.designerVariant.scale_factor.height;
                output_height = this.designerVariant.output_image.width / this.designerVariant.scale_factor.width;
            } else {
                output_width = this.designerVariant.output_image.width / this.designerVariant.scale_factor.width;
                output_height = this.designerVariant.output_image.height / this.designerVariant.scale_factor.height;
            }

            canvas.width = output_width;
            canvas.height = output_height;

            // Create a new Image object to ensure proper loading
            const tempImage = new Image();
            tempImage.src = base64;

            tempImage.onload = () => {
                // Draw the image onto the resized canvas
                ctx.drawImage(tempImage, 0, 0, output_width, output_height);

                // Update the image source with the resized version
                imgRef.src = canvas.toDataURL("image/png");
            };
        },

        // Decode a base64 data URL into a Blob containing the exact original
        // bytes. The distorted image is already a byte-exact PNG produced by
        // the worker's pure-JS encoder, so we upload/download those bytes
        // verbatim. Decoding through an <img> and re-encoding via canvas would
        // re-introduce ±1 per-channel noise under browser anti-fingerprinting
        // (Firefox RFP/FPP, Brave farbling) because every canvas read-back is
        // perturbed. Going straight from base64 to bytes avoids the canvas.
        dataUrlToBlob(dataUrl) {
            const commaIndex = dataUrl.indexOf(",");
            const header = dataUrl.slice(0, commaIndex);
            const base64 = dataUrl.slice(commaIndex + 1);

            const mimeMatch = /data:([^;]+)/.exec(header);
            const mime = mimeMatch ? mimeMatch[1] : "image/png";

            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }

            return new Blob([bytes], { type: mime });
        },

        // Return the current distorted image as a PNG Blob, byte-for-byte
        // identical to the worker output. Shared by the upload flow.
        getCanvasBlob() {
            const dataUrl = this.getImageBase64();
            if (!dataUrl || dataUrl.indexOf("data:") !== 0) {
                return Promise.reject(new Error("Distorted image is not a data URL"));
            }
            return Promise.resolve(this.dataUrlToBlob(dataUrl));
        },

        // Previous implementation: round-tripped the data URL through an <img>
        // and canvas.toBlob. The canvas read-back is perturbed by browser
        // anti-fingerprinting (Firefox RFP/FPP, Brave farbling), injecting ±1
        // per-channel noise and thus non-palette colors into the upload.
        // getCanvasBlob() {
        //     return new Promise((resolve, reject) => {
        //         const image = new Image();
        //
        //         image.onload = () => {
        //             const canvas = document.createElement("canvas");
        //             const ctx = canvas.getContext("2d");
        //
        //             canvas.width = image.width;
        //             canvas.height = image.height;
        //             ctx.drawImage(image, 0, 0);
        //
        //             canvas.toBlob((blob) => {
        //                 if (blob) {
        //                     resolve(blob);
        //                 } else {
        //                     reject(new Error("canvas.toBlob returned null"));
        //                 }
        //             }, "image/png");
        //         };
        //
        //         image.onerror = (err) => reject(err);
        //
        //         image.src = this.getImageBase64();
        //     });
        // },

        getBitmapBase64() {
            return new Promise((resolve, reject) => {
                // Create a new image element
                const image = new Image();
                image.src = this.getImageBase64();

                // When the image is loaded, draw it onto the canvas
                image.onload = () => {
                    // Create a canvas element
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");

                    // Set canvas size to the image size
                    canvas.width = image.width;
                    canvas.height = image.height;

                    // Draw the image onto the canvas
                    ctx.drawImage(image, 0, 0);

                    // Get the image data from the canvas (RGBA format)
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const pixelData = imageData.data;
                    const bmpData = this.convertToBmp(canvas.width, canvas.height, pixelData);

                    // Return BMP data as Base64
                    const base64Bmp = "data:image/bmp;base64," + Buffer.from(bmpData).toString("base64");
                    resolve(base64Bmp);
                };

                image.onerror = (err) => {
                    console.error(err);
                };
            });
        },

        downloadBitmap() {
            // Create a new image element
            const image = new Image();

            // When the image is loaded, draw it onto the canvas
            image.onload = () => {
                // Create a canvas element
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                // Set canvas size to the image size
                canvas.width = image.width;
                canvas.height = image.height;

                // Draw the image onto the canvas
                ctx.drawImage(image, 0, 0);

                // Get the image data from the canvas (RGBA format)
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const pixelData = imageData.data;
                const bmpData = this.convertToBmp(canvas.width, canvas.height, pixelData);

                // Convert the canvas to a BMP format (we'll use image/bmp mime type)
                const blob = new Blob([bmpData], { type: "image/bmp" });

                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "image.bmp";
                link.click();
            };

            image.src = this.getImageBase64();
        },

        downloadImage() {
            // Download the byte-exact PNG (same bytes uploaded to the server),
            // straight from the data URL with no <img>/canvas re-encode, so the
            // file faithfully preserves the exact palette (no anti-fingerprinting
            // canvas noise).
            const dataUrl = this.getImageBase64();
            if (!dataUrl || dataUrl.indexOf("data:") !== 0) {
                console.error("Distorted image is not a data URL");
                return;
            }

            const blob = this.dataUrlToBlob(dataUrl);
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "image.png";
            link.click();

            // Cleanup
            URL.revokeObjectURL(link.href);
        },

        // Previous implementation: round-tripped the data URL through an <img>
        // and canvas.toBlob, which re-introduces ±1 canvas read-back noise
        // (non-palette colors) under browser anti-fingerprinting.
        // downloadImage() {
        //     // Create a new image element
        //     const image = new Image();
        //
        //     // Ensure cross-origin images are handled properly (optional)
        //     image.crossOrigin = "anonymous";
        //
        //     image.onload = () => {
        //         // Create a canvas element
        //         const canvas = document.createElement("canvas");
        //         const ctx = canvas.getContext("2d");
        //
        //         // Set canvas size to the image size
        //         canvas.width = image.width;
        //         canvas.height = image.height;
        //
        //         // Draw the image onto the canvas
        //         ctx.drawImage(image, 0, 0);
        //
        //         // Convert canvas content to a Blob
        //         canvas.toBlob((blob) => {
        //             if (blob) {
        //                 const link = document.createElement("a");
        //                 link.href = URL.createObjectURL(blob);
        //                 link.download = "image.png";
        //                 link.click();
        //
        //                 // Cleanup
        //                 URL.revokeObjectURL(link.href);
        //             }
        //         }, "image/png");
        //     };
        //
        //     // Set image source after defining onload
        //     image.src = this.getImageBase64();
        // },

        convertToBmp(width, height, pixelData) {
            //rearrage from RGBA to BGRA
            for (let i = 0; i < pixelData.length; i += 4) {
                const r = pixelData[i];
                const g = pixelData[i + 1];
                const b = pixelData[i + 2];
                const a = pixelData[i + 3];

                pixelData[i] = a;
                pixelData[i + 1] = b;
                pixelData[i + 2] = g;
                pixelData[i + 3] = r;
            }

            const bmpData = bmp.encode({
                width: width,
                height: height,
                data: pixelData,
            });
            return bmpData.data; // Return the BMP binary data
        },

        getQuantity() {
            const quantity = this.$refs.quantityInputField.value;
            if (quantity === "" || isNaN(quantity) || quantity < 1) {
                return 1;
            } else {
                return Number(quantity);
            }
        },

        getColorPalette() {
            // Get color name and hex value in  the format Red(#FF0000) as string for the color palette
            var colorPaletteStrings = [];
            for (let i = 0; i < this.colorPalette.length; i++) {
                const color = this.config.colors.find((color) => color.hex === this.colorPalette[i]);
                if (color) {
                    colorPaletteStrings.push(color.name + " (" + color.hex + ")");
                }
            }

            return colorPaletteStrings.join(" | ");
        },

        // Upload the design PNG to the server, then submit the add-to-cart form
        // referencing only the returned UUID (no multi-MB base64 in the request).
        async addToCart() {
            if (this.isUploading) {
                return;
            }

            this.uploadError = null;
            this.isUploading = true;

            try {
                const wpData = window.topodeckiWpData;
                if (!wpData || !wpData.restUrl) {
                    throw new Error("Upload endpoint not configured (topodeckiWpData missing)");
                }

                // Render the current design to a PNG blob.
                const blob = await this.getCanvasBlob();

                // Upload the blob to the REST endpoint.
                const formData = new FormData();
                formData.append("image", blob, "design.png");

                const response = await fetch(wpData.restUrl, {
                    method: "POST",
                    headers: {
                        "X-WP-Nonce": wpData.nonce,
                    },
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error("Upload failed with status " + response.status);
                }

                const result = await response.json();
                if (!result || !result.uuid) {
                    throw new Error("Upload response did not contain a uuid");
                }

                // Upload succeeded — submit the add-to-cart form with the UUID.
                this.submitCartForm(result.uuid);
            } catch (err) {
                console.error("Add to cart failed", err);
                this.uploadError =
                    this.config.texts.previewView.uploadErrorText ||
                    "Das Bild konnte nicht hochgeladen werden. Bitte versuchen Sie es erneut.";
                this.isUploading = false;
            }
        },

        // Build and submit the WooCommerce add-to-cart form. References the
        // uploaded design via its UUID instead of an inline base64 image.
        submitCartForm(imageUuid) {
            const addToCartUrl = this.config.product.addToCartUrl;
            const url = addToCartUrl === "" ? window.location.href : addToCartUrl;
            const productId = this.config.product.productId;
            const colors = this.getColorPalette();
            const quantity = this.getQuantity();

            // Create a form dynamically
            const form = document.createElement("form");
            form.method = "POST";
            form.action = url;

            // Add the product ID field
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "add-to-cart";
            input.value = productId;
            form.appendChild(input);

            // Add the quantity field
            const quantityInput = document.createElement("input");
            quantityInput.type = "hidden";
            quantityInput.name = "quantity";
            quantityInput.value = quantity;
            form.appendChild(quantityInput);

            // Add the uploaded image UUID field
            const uuidInput = document.createElement("input");
            uuidInput.type = "hidden";
            uuidInput.name = "topodecki_image_uuid";
            uuidInput.value = imageUuid;
            form.appendChild(uuidInput);

            // Add the color palette field
            const colorInput = document.createElement("input");
            colorInput.type = "hidden";
            colorInput.name = "color";
            colorInput.value = colors;
            form.appendChild(colorInput);

            // Append the form to the body and submit it
            document.body.appendChild(form);
            form.submit();
        },

        async initControl(base64Image, colorPalette, isPortrait = false) {
            this.isPortrait = isPortrait;
            this.colorPalette = colorPalette;

            // Set image
            this.distortedImage = new Image();
            this.distortedImage.src = base64Image;
            this.distortedImage.onload = () => {
                this.updateImage();
            };
        },
    },
};
</script>

<style>
.pc-preview-control-content {
    display: flex;
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1em;
    min-height: 70vh;
    position: relative;
    /* border: 1px solid #00f; */
}

.pc-loading-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.7);
    z-index: 10;
}

.pc-spinner {
    width: 48px;
    height: 48px;
    border: 5px solid rgba(17, 17, 17, 0.2);
    border-top-color: #111111;
    border-radius: 50%;
    animation: pc-spin 0.8s linear infinite;
}

@keyframes pc-spin {
    to {
        transform: rotate(360deg);
    }
}

.pc-info-container {
    flex-grow: 1;
    max-width: 500px;
    /* border: 1px solid #f00; */
}

.pc-image-container {
    flex-grow: 1;
    position: relative;
    display: inline-block;
    max-height: 50vh;
    max-width: 50vw;

    /* border: 1px solid #000; */
}
.pc-image-wrapper {
    max-height: 100%;
    max-width: 100%;
    min-height: 60vh;
    display: block;
    margin: auto;
    background-color: #f0f0f0;
    /* border: 1px solid #f00; */
}

.pc-image {
    max-width: 100%;
    max-height: 100%;
    display: block;
    margin: auto;
    /* border: 1px solid #0f0; */
}

.pc-upload-error {
    margin-top: 1em;
    padding: 0.75em 1em;
    color: #a30000;
    background-color: #fde8e8;
    border: 1px solid #a30000;
    border-radius: 5px;
}

.pc-download-button {
    margin-top: 1em;
    padding: 0.5em 1em;
    color: #ffffff;
    background-color: #111111;
    border: 1px solid #111111;
    border-radius: 5px;
    cursor: pointer;
}

.pc-download-button:hover {
    opacity: 0.85;
}

/* .testing {
    color: red;
} */
</style>